"use client"

import { CreditsTag, MiniJobCard } from "@/components"
import { Avatar, BorderedInput, Button, Separator, Text, TextArea } from "@/elements"
import { FragmentType, graphql, useFragment } from "@/gql"
import { JobApplicationProjectPreviewFragmentDoc, JobPostingPayRate, JobPostingPayType } from "@/gql/graphql"
import type { NovaSession } from "@/types"
import { PlusIcon } from "@heroicons/react/24/outline"
import { ApplyToJobContentSkeleton } from "./ApplyToJobContentSkeleton"
import { HowCreditsWork } from "./HowCreditsWork"

import { ampli } from "@/ampli"
import { EmptyState } from "@/components/EmptyState/EmptyState"
import { AdditionalProjectsCard, ProjectPreviewCard } from "@/components/JobApplication"
import { getFragmentData } from "@/gql-clients/experimental/getFragmentData"
import { ImageX } from "@/icons/outline"
import { formatCurrencyLocale } from "@/utils/formatCurrency"
import { useEffect, useMemo, useState } from "react"
import { PayFrequencyDropdown } from "./PayFrequencyDropdown"

const ApplyToJobContentJobPostingFragment = graphql(`
  fragment ApplyToJobContentJobPosting on JobPosting {
    __typename
    id
    ...MiniJobCard
    position {
      name
      id
    }

    payType
    payRate
    payString

    owner {
      id
      name
      avatarInitials
      ... on HasAvatarUrl {
        avatarUrl(width: 48)
      }
    }
  }
`)

const ApplyToJobContentCreativeFragment = graphql(`
  fragment ApplyToJobContentCreative on IndividualCreative {
    id
    __typename
    name
    avatarInitials
    avatarUrl(width: 80)
    creditsCount: creativeWorkPortfolioProjects(creatorRoles: [$positionId], status: PUBLISHED, first: 1)
      @connection(key: "creditsCount") {
      totalCount
    }
    emptyStateCheck: creativeWorkPortfolioProjects(status: PUBLISHED, first: 0) {
      totalCount
    }
    ...AdditionalProjectsForApplication
  }
`)

const getDayRateLabel = (
  payType: JobPostingPayType | undefined,
  payRate: JobPostingPayRate | undefined,
  payString: string | null | undefined,
  { titleCase = false } = {}
) => {
  const formatText = (text: string) => (titleCase ? text : text.toLowerCase())

  switch (payType) {
    case JobPostingPayType.RateBased:
      if (payRate && payString !== "$TBD") {
        const formattedPayRate = payRate.replaceAll("_", " ")
        const rateText = `${formattedPayRate[0].toUpperCase()}${formattedPayRate.toLowerCase().slice(1)} rate`
        return formatText(rateText)
      }
    default:
      return formatText("Proposed rate")
  }
}

export type FormField = {
  value: string
  error: string | undefined
  onChange: (text: string) => void
  setError: (error: string | undefined) => void
}

export type ProposedRateForm = {
  payRateFrequency: JobPostingPayRate | undefined
  setPayRateFrequency: (payRateFrequency: JobPostingPayRate | undefined) => void
  payType: JobPostingPayType | undefined
  setPayType: (payType: JobPostingPayType | undefined) => void
} & FormField

interface Props {
  onClose?: () => void
  loading: boolean
  coverLetter: FormField
  proposedRate: ProposedRateForm
  creative: FragmentType<typeof ApplyToJobContentCreativeFragment> | undefined | null
  jobPosting: FragmentType<typeof ApplyToJobContentJobPostingFragment> | undefined | null
  serverSession: NovaSession | null
  onSelectWork: () => void
  creativeWorkAttachments: FragmentType<typeof JobApplicationProjectPreviewFragmentDoc>[]
  hasViewedSelectionScreen: boolean
}

const formatUSDInput = (value: string): string => {
  const cleaned = value.replace(/[^\d.]/g, "")

  // only one decimal point
  const parts = cleaned.split(".")
  if (parts.length > 2) {
    return parts[0] + "." + parts.slice(1).join("")
  }

  // 2 decimal places
  if (parts[1]?.length > 2) {
    return parts[0] + "." + parts[1].slice(0, 2)
  }

  // max lenght
  if (parts[0]?.length > 6) {
    return cleaned.slice(0, 6)
  }

  return cleaned
}

const isValidUSD = (value: string): boolean => {
  if (!value) return true // Allow empty value
  const num = parseFloat(value)
  return !isNaN(num) && num >= 0 && num <= 999999.99
}

export const ApplyToJobContent: React.FC<Props> = ({
  creativeWorkAttachments,
  coverLetter, // should change this to a form so that i dont have to pass this in
  proposedRate,
  loading,
  serverSession,
  onSelectWork,
  hasViewedSelectionScreen,
  ...props
}) => {
  const jobPosting = useFragment(ApplyToJobContentJobPostingFragment, props.jobPosting)
  const creative = useFragment(ApplyToJobContentCreativeFragment, props.creative)

  const [proposedRatePlaceholder, setProposedRatePlaceholder] = useState("")

  useEffect(() => {
    setProposedRatePlaceholder(`Add rate`)
  }, [jobPosting?.payType, jobPosting?.payRate, jobPosting?.payString])

  const handleCoverLetter = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    coverLetter.onChange(e.target.value)
  }

  const handleRateChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const formatted = formatUSDInput(e.target.value)
    proposedRate.onChange(formatted)
    if (jobPosting?.payString !== "$TBD") {
      proposedRate.setPayRateFrequency(jobPosting?.payRate)
      proposedRate.setPayType(jobPosting?.payType)
    }

    if (formatted && !isValidUSD(formatted)) {
      proposedRate.setError("Please enter a valid amount between $0 and $999,999.99")
    } else {
      proposedRate.setError(undefined)
    }
  }

  const selectedIds = useMemo(
    () => creativeWorkAttachments.map(cw => getFragmentData(JobApplicationProjectPreviewFragmentDoc, cw).id),
    [creativeWorkAttachments]
  )

  if (loading) {
    return <ApplyToJobContentSkeleton />
  }

  return (
    <div className="mt-6 flex flex-col">
      <div className="px-5 md:px-0">
        <MiniJobCard size="large" jobPosting={jobPosting} />
      </div>

      <div className="py-6">
        <Separator />
      </div>
      <div className="flex flex-row items-center gap-x-2 px-5 md:px-0">
        <Text className="text-fg-2">This is what</Text>
        <Avatar size="xs" src={jobPosting?.owner.avatarUrl} initials={jobPosting?.owner.avatarInitials} />
        <Text className="text-fg-2">
          <Text element="span" className="text-fg-1">
            {jobPosting?.owner.name}
          </Text>{" "}
          will see.
        </Text>
      </div>

      <div className="my-6 flex w-full items-end justify-between px-5 md:px-0">
        <div className="flex flex-row items-center gap-x-3">
          <Avatar size="lg" src={creative?.avatarUrl} initials={creative?.avatarInitials} />
          <div className="flex w-full flex-col gap-y-0">
            <Text>{creative?.name}</Text>

            <CreditsTag
              creditsCount={creative?.creditsCount.totalCount}
              positionName={jobPosting?.position?.name}
              textVariant="body3"
            />
          </div>
        </div>
        {!hasViewedSelectionScreen && creativeWorkAttachments.length === 0 ? (
          <div className="hidden flex-shrink-0 md:flex">
            <HowCreditsWork />
          </div>
        ) : (
          <Button
            variant="secondary"
            onClick={() => {
              ampli.selectWorkEdit()
              onSelectWork?.()
            }}>
            Edit
          </Button>
        )}
      </div>
      {creative?.emptyStateCheck.totalCount === 0 ? (
        <div className="px-5 md:px-0">
          <EmptyState Icon={ImageX} title="No projects" description="You don't have any projects in your profile" />
        </div>
      ) : creativeWorkAttachments.length === 0 && !hasViewedSelectionScreen ? (
        <div className="px-5 md:px-0">
          <div
            onClick={() => {
              ampli.selectWorkStart()
              onSelectWork?.()
            }}
            className="flex h-[180px] w-full cursor-pointer flex-col items-center justify-center rounded-md border border-bg-3 bg-bg-2">
            <PlusIcon className="size-6 stroke-fg-3" />
            <Text className="text-fg-3">Select work</Text>
          </div>
        </div>
      ) : (
        <div className="flex flex-row gap-x-2 overflow-x-auto px-5 md:w-[calc(100%+40px)] md:px-0 md:pr-10">
          {creativeWorkAttachments.map((project, index) => {
            return (
              <ProjectPreviewCard
                key={index}
                projectData={project}
                positionId={jobPosting?.position?.id}
                imageWidth="200px"
              />
            )
          })}

          <AdditionalProjectsCard creative={creative} selectedProjectsIds={selectedIds} />
        </div>
      )}

      <div className="mt-6 flex w-full flex-col px-5 md:px-0">
        <div className="flex flex-row items-center gap-x-2">
          <div className="flex flex-1 flex-col">
            <Text variant="label">
              {getDayRateLabel(jobPosting?.payType, jobPosting?.payRate, jobPosting?.payString, { titleCase: true })}
            </Text>

            <div className="relative mt-2 flex flex-col">
              <BorderedInput
                value={formatCurrencyLocale(proposedRate.value, { decimals: 0 })}
                placeholder={proposedRatePlaceholder}
                onChange={e => {
                  const rawValue = e.target.value
                  const numericValue = parseFloat(rawValue.replace(/,/g, "")) || null

                  if (numericValue !== null && numericValue > 999999) {
                    proposedRate.setError("Rate cannot be greater than 999,999")
                    return
                  } else if (numericValue) {
                    proposedRate.onChange(numericValue.toString())
                  } else {
                    proposedRate.onChange("0")
                  }
                }}
                onFocus={() => {
                  setProposedRatePlaceholder("1200")
                }}
                onBlur={() => {
                  setProposedRatePlaceholder(`Add rate`)
                }}
                className={"w-full bg-bg-1 pl-[32px]"}
              />
              <Text className="absolute left-4 top-1/2 -translate-y-1/2 text-fg-3">$</Text>
              {jobPosting?.payType === JobPostingPayType.RateBased && jobPosting?.payString !== "$TBD" ? (
                <Text className="absolute right-4 top-1/2 -translate-y-1/2 text-fg-3">
                  /{jobPosting.payRate.replaceAll("_", " ").toLowerCase()}
                </Text>
              ) : null}
            </div>
          </div>
          {jobPosting?.payString === "$TBD" ? (
            <div className="flex h-auto flex-1 flex-col">
              <Text variant="label">Pay Frequency</Text>

              <PayFrequencyDropdown {...proposedRate} />
            </div>
          ) : null}
        </div>

        {proposedRate.error && (
          <Text variant="body3" className="mt-1 text-red-default">
            {proposedRate.error}
          </Text>
        )}
        <Text variant="label" className="mt-8" id="add-note-section">
          Add a note*
        </Text>
        <TextArea
          value={coverLetter.value}
          onChange={handleCoverLetter}
          className="mt-3 w-full rounded-lg bg-bg-1"
          placeholder="Attach any information that will help you stand out"
          rows={4}
        />

        {coverLetter.error ? (
          <Text variant="body3" className="text-red-default">
            {coverLetter.error}
          </Text>
        ) : null}
      </div>
    </div>
  )
}
